import { useCallback, useEffect, useRef } from 'react'
import { animate, motion, useMotionValue, useTransform } from 'framer-motion'
import { MAX_TILT_DEG } from './cardTilt'
const EASE = [0.16, 1, 0.3, 1]
const MAX_SCALE = 5
const DOUBLE_TAP_SCALE = 2.5
const TAP_SLOP = 10
const TAP_MAX_MS = 350
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_DISTANCE = 40
const AXIS_LOCK_DISTANCE = 10
const DISMISS_DISTANCE = 120
const DISMISS_VELOCITY = 0.6
const PAGE_VELOCITY = 0.45
const TILT_SPRING = { type: 'spring', stiffness: 240, damping: 22 }
/** Dragging sideways across the card's own width turns it this many degrees. */
const TURN_DEGREES_PER_CARD_WIDTH = 200
/** How long (ms) of release velocity carries the card when it is flicked. */
const TURN_FLICK_MS = 140
const TURN_SETTLE = { duration: 0.6, ease: EASE }
const clampTilt = (value) => Math.max(-MAX_TILT_DEG, Math.min(MAX_TILT_DEG, value))
function ZoomStage({
  children,
  onClose,
  onPrev,
  onNext,
  tilt,
  turn,
  canTurn = false,
  paddingTop,
  paddingBottom,
}) {
  const stageRef = useRef(null)
  const contentRef = useRef(null)
  const swipeX = useMotionValue(0)
  const dismissY = useMotionValue(0)
  const panX = useMotionValue(0)
  const panY = useMotionValue(0)
  const scale = useMotionValue(1)
  const dismissOpacity = useTransform(dismissY, [0, 280], [1, 0.4])
  const pointers = useRef(/* @__PURE__ */ new Map())
  const lastTap = useRef(null)
  const gesture = useRef({
    mode: 'idle',
    axis: null,
    startX: 0,
    startY: 0,
    startTime: 0,
    baseX: 0,
    baseY: 0,
    startDistance: 1,
    startScale: 1,
    turnBase: 0,
    startMidX: 0,
    startMidY: 0,
    moved: 0,
    lastX: 0,
    lastY: 0,
    lastTime: 0,
    vx: 0,
    vy: 0,
  })
  const stageCenter = () => {
    const rect = stageRef.current?.getBoundingClientRect()
    return rect
      ? { cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2 }
      : { cx: 0, cy: 0 }
  }
  const clampPan = useCallback((s, tx, ty) => {
    const stage = stageRef.current
    const content = contentRef.current
    if (!stage || !content || s <= 1) return { x: 0, y: 0 }
    const maxX = Math.max(0, (content.offsetWidth * s - stage.clientWidth) / 2)
    const maxY = Math.max(0, (content.offsetHeight * s - stage.clientHeight) / 2)
    return {
      x: Math.min(maxX, Math.max(-maxX, tx)),
      y: Math.min(maxY, Math.max(-maxY, ty)),
    }
  }, [])
  const settle = useCallback(
    (targetScale, tx, ty) => {
      const s = Math.min(MAX_SCALE, Math.max(1, targetScale))
      const clamped = clampPan(s, tx, ty)
      const transition = { duration: 0.3, ease: EASE }
      animate(scale, s, transition)
      animate(panX, clamped.x, transition)
      animate(panY, clamped.y, transition)
    },
    [clampPan, panX, panY, scale]
  )
  const zoomAround = useCallback(
    (fx, fy, nextScale) => {
      const s0 = scale.get()
      const { cx, cy } = stageCenter()
      const tx = fx - cx - (nextScale / s0) * (fx - cx - panX.get())
      const ty = fy - cy - (nextScale / s0) * (fy - cy - panY.get())
      return { tx, ty }
    },
    [panX, panY, scale]
  )
  const stopAnimations = () => {
    scale.stop()
    panX.stop()
    panY.stop()
    swipeX.stop()
    dismissY.stop()
    turn?.stop()
    tilt?.x.stop()
    tilt?.y.stop()
  }
  const releaseTilt = () => {
    if (!tilt) return
    animate(tilt.x, 0, TILT_SPRING)
    animate(tilt.y, 0, TILT_SPRING)
  }
  const tiltBy = (dx, dy) => {
    const rect = contentRef.current?.getBoundingClientRect()
    if (!tilt || !rect || rect.width === 0 || rect.height === 0) return
    tilt.y.set(clampTilt((dx / rect.width) * MAX_TILT_DEG * 2))
    tilt.x.set(clampTilt((-dy / rect.height) * MAX_TILT_DEG * 2))
  }
  const toggleZoom = (fx, fy) => {
    if (scale.get() > 1.2) {
      settle(1, 0, 0)
      return
    }
    const { tx, ty } = zoomAround(fx, fy, DOUBLE_TAP_SCALE)
    settle(DOUBLE_TAP_SCALE, tx, ty)
  }
  const handleTap = (clientX, clientY) => {
    const now = performance.now()
    const previous = lastTap.current
    if (
      previous &&
      now - previous.t < DOUBLE_TAP_MS &&
      Math.hypot(clientX - previous.x, clientY - previous.y) < DOUBLE_TAP_DISTANCE
    ) {
      lastTap.current = null
      toggleZoom(clientX, clientY)
      return
    }
    lastTap.current = { t: now, x: clientX, y: clientY }
    const rect = contentRef.current?.getBoundingClientRect()
    const measured = !!rect && rect.width > 0 && rect.height > 0
    const outside =
      measured &&
      (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom)
    if (outside) onClose()
  }
  const beginSingle = (clientX, clientY) => {
    const g = gesture.current
    g.mode = scale.get() > 1.02 ? 'pan' : 'swipe'
    g.axis = null
    g.startX = clientX
    g.startY = clientY
    g.startTime = performance.now()
    g.baseX = panX.get()
    g.baseY = panY.get()
    g.turnBase = turn ? turn.get() : 0
    g.moved = 0
    g.lastX = clientX
    g.lastY = clientY
    g.lastTime = g.startTime
    g.vx = 0
    g.vy = 0
  }
  const onPointerDown = (event) => {
    stageRef.current?.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    stopAnimations()
    const g = gesture.current
    if (pointers.current.size === 1) {
      beginSingle(event.clientX, event.clientY)
    } else if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values())
      g.mode = 'pinch'
      releaseTilt()
      g.startDistance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y))
      g.startScale = scale.get()
      g.startMidX = (a.x + b.x) / 2
      g.startMidY = (a.y + b.y) / 2
      g.baseX = panX.get()
      g.baseY = panY.get()
      g.moved = Number.POSITIVE_INFINITY
      swipeX.set(0)
      dismissY.set(0)
    }
  }
  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) {
      if (tilt && event.pointerType === 'mouse' && scale.get() <= 1.02) {
        const rect = contentRef.current?.getBoundingClientRect()
        if (rect && rect.width > 0 && rect.height > 0) {
          tiltBy(
            event.clientX - (rect.left + rect.width / 2),
            event.clientY - (rect.top + rect.height / 2)
          )
        }
      }
      return
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const g = gesture.current
    if (g.mode === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = Array.from(pointers.current.values())
      const distance = Math.hypot(a.x - b.x, a.y - b.y)
      const s = Math.min(
        MAX_SCALE * 1.15,
        Math.max(0.85, g.startScale * (distance / g.startDistance))
      )
      const midX = (a.x + b.x) / 2
      const midY = (a.y + b.y) / 2
      const { cx, cy } = stageCenter()
      const ratio = s / g.startScale
      scale.set(s)
      if (s >= 1) {
        const next = clampPan(
          s,
          midX - cx - ratio * (g.startMidX - cx - g.baseX),
          midY - cy - ratio * (g.startMidY - cy - g.baseY)
        )
        panX.set(next.x)
        panY.set(next.y)
      }
      return
    }
    const dx = event.clientX - g.startX
    const dy = event.clientY - g.startY
    g.moved = Math.max(g.moved, Math.hypot(dx, dy))
    const now = performance.now()
    const dt = Math.max(1, now - g.lastTime)
    g.vx = (event.clientX - g.lastX) / dt
    g.vy = (event.clientY - g.lastY) / dt
    g.lastX = event.clientX
    g.lastY = event.clientY
    g.lastTime = now
    if (g.mode === 'pan') {
      const next = clampPan(scale.get(), g.baseX + dx, g.baseY + dy)
      panX.set(next.x)
      panY.set(next.y)
      return
    }
    if (g.mode === 'swipe' && tilt && turn && canTurn) {
      // Dragging sideways turns the card over; dragging up/down only leans it.
      const rect = contentRef.current?.getBoundingClientRect()
      const cardWidth = contentRef.current?.offsetWidth || stageRef.current?.clientWidth || 360
      const base = Math.round(g.turnBase / 180) * 180
      turn.set(
        Math.min(
          base + 180,
          Math.max(base - 180, g.turnBase + (dx / cardWidth) * TURN_DEGREES_PER_CARD_WIDTH)
        )
      )
      if (rect && rect.height > 0) {
        tilt.y.set(0)
        tilt.x.set(clampTilt((-dy / rect.height) * MAX_TILT_DEG * 2))
      }
      return
    }
    if (g.mode === 'swipe' && tilt) {
      tiltBy(dx, dy)
      return
    }
    if (g.mode === 'swipe') {
      if (g.axis === null && Math.hypot(dx, dy) > AXIS_LOCK_DISTANCE) {
        g.axis = Math.abs(dx) > Math.abs(dy) || dy < 0 ? 'h' : 'v'
      }
      if (g.axis === 'h') {
        const hasTarget = dx < 0 ? !!onNext : !!onPrev
        swipeX.set(hasTarget ? dx : dx * 0.25)
      } else if (g.axis === 'v') {
        dismissY.set(Math.max(0, dy))
      }
    }
  }
  const finishSwipe = () => {
    const g = gesture.current
    const stageWidth = stageRef.current?.clientWidth ?? 360
    const dx = swipeX.get()
    if (g.axis === 'h') {
      const far = Math.abs(dx) > Math.min(100, stageWidth * 0.22)
      const fast = Math.abs(g.vx) > PAGE_VELOCITY
      const forward = dx < 0
      if ((far || fast) && (forward ? onNext : onPrev)) {
        swipeX.set(0)
        ;(forward ? onNext : onPrev)?.()
        return
      }
      animate(swipeX, 0, { duration: 0.3, ease: EASE })
    } else if (g.axis === 'v') {
      if (dismissY.get() > DISMISS_DISTANCE || g.vy > DISMISS_VELOCITY) {
        onClose()
        return
      }
      animate(dismissY, 0, { duration: 0.3, ease: EASE })
    }
  }
  const finishTurn = () => {
    if (!turn) return
    const g = gesture.current
    const cardWidth = contentRef.current?.offsetWidth || stageRef.current?.clientWidth || 360
    const base = Math.round(g.turnBase / 180) * 180
    const degreesPerPixel = TURN_DEGREES_PER_CARD_WIDTH / cardWidth
    const projected = turn.get() + g.vx * TURN_FLICK_MS * degreesPerPixel
    const target = Math.min(base + 180, Math.max(base - 180, Math.round(projected / 180) * 180))
    animate(turn, target, TURN_SETTLE)
    if (tilt) {
      animate(tilt.x, 0, TILT_SPRING)
      animate(tilt.y, 0, TILT_SPRING)
    }
  }
  const onPointerEnd = (event) => {
    if (!pointers.current.has(event.pointerId)) return
    pointers.current.delete(event.pointerId)
    const g = gesture.current
    if (g.mode === 'pinch') {
      if (pointers.current.size === 0) {
        g.mode = 'idle'
        settle(scale.get(), panX.get(), panY.get())
      } else if (pointers.current.size === 1) {
        const [remaining] = Array.from(pointers.current.values())
        beginSingle(remaining.x, remaining.y)
        g.mode = scale.get() > 1.02 ? 'pan' : 'idle'
        g.moved = Number.POSITIVE_INFINITY
      }
      return
    }
    if (pointers.current.size > 0) return
    if (event.pointerType !== 'mouse') releaseTilt()
    const wasQuick = performance.now() - g.startTime < TAP_MAX_MS
    if (g.moved < TAP_SLOP && wasQuick && event.type === 'pointerup') {
      g.mode = 'idle'
      handleTap(event.clientX, event.clientY)
      return
    }
    if (g.mode === 'swipe') {
      if (turn && canTurn && tilt) finishTurn()
      else finishSwipe()
    }
    g.mode = 'idle'
  }
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const onWheel = (event) => {
      event.preventDefault()
      const s0 = scale.get()
      const s1 = Math.min(MAX_SCALE, Math.max(1, s0 * Math.exp(-event.deltaY * 2e-3)))
      if (s1 === s0) return
      if (s1 > 1.02) releaseTilt()
      if (s1 === 1) {
        scale.set(1)
        panX.set(0)
        panY.set(0)
        return
      }
      const { tx, ty } = zoomAround(event.clientX, event.clientY, s1)
      const clamped = clampPan(s1, tx, ty)
      scale.set(s1)
      panX.set(clamped.x)
      panY.set(clamped.y)
    }
    stage.addEventListener('wheel', onWheel, { passive: false })
    return () => stage.removeEventListener('wheel', onWheel)
  }, [clampPan, panX, panY, scale, zoomAround])
  return (
    <div
      ref={stageRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse' && pointers.current.size === 0) releaseTilt()
      }}
      className="absolute inset-0 flex touch-none select-none items-center justify-center overflow-hidden"
      style={{ paddingTop, paddingBottom, paddingInline: 8 }}
    >
      <motion.div style={{ x: swipeX, y: dismissY, opacity: dismissOpacity }}>
        <motion.div style={{ x: panX, y: panY, scale }}>
          <div ref={contentRef}>{typeof children === 'function' ? children(scale) : children}</div>
        </motion.div>
      </motion.div>
    </div>
  )
}
export { ZoomStage as default }
